import { useProgress } from "@react-three/drei";
import { useEffect, useRef, useState, useMemo } from "react";
import { useGameStore } from "../core/store/gameStore";
import gsap from "gsap";

// --- Sub Components ---
const Key = ({ children }: { children: React.ReactNode }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: '18px', height: '22px', padding: '0 5px', margin: '0 4px',
        border: '1px solid #555', borderRadius: '4px', background: 'rgba(255,255,255,0.05)',
        fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 'bold', color: '#ccc',
        lineHeight: 1, verticalAlign: 'middle', boxSizing: 'border-box'
    }}>
        {children}
    </span>
);

const MouseIcon = () => (
    <span style={{
        display: 'inline-block', position: 'relative', width: '12px', height: '18px', margin: '0 4px',
        border: '1.5px solid #ccc', borderRadius: '6px', verticalAlign: 'middle', opacity: 0.8
    }}>
        <span style={{
            position: 'absolute', top: '3px', left: '50%', transform: 'translateX(-50%)',
            width: '1.5px', height: '4px', background: '#ccc', borderRadius: '1px'
        }} />
    </span>
);

const InstructionRow = ({ input, label }: { input: React.ReactNode, label: string }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        {input}
        <span style={{ marginLeft: '6px', fontSize: '0.7rem', letterSpacing: '1px', fontWeight: 500, transform: 'translateY(1px)' }}>
            {label}
        </span>
    </div>
);

// --- Main Component ---

export function LoadingScreen() {
    // Store & Hooks
    const { active, progress: downloadProgress } = useProgress();
    const activeTargets = useGameStore((state) => state.activeTargets);
    const readyStatus = useGameStore((state) => state.readyStatus);
    const isMobile = useGameStore((state) => state.isMobile);
    const setIsGameStarted = useGameStore((state) => state.setIsGameStarted);
    const gpuError = useGameStore((state) => state.gpuError);
    const fromStar = useGameStore((state) => state.fromStar);

    // Local State
    const [isReadyToStart, setIsReadyToStart] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [isLandscape, setIsLandscape] = useState(false); // New State for Landscape detection

    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<gsap.core.Tween | null>(null);

    // Orientation detection hook
    useEffect(() => {
        const checkOrientation = () => {
            setIsLandscape(window.innerWidth > window.innerHeight);
        };
        checkOrientation();
        window.addEventListener('resize', checkOrientation);
        return () => window.removeEventListener('resize', checkOrientation);
    }, []);

    const total = activeTargets.length;
    const loaded = activeTargets.filter((id) => readyStatus[id]).length;
    const compileProgress = total === 0 ? 0 : (loaded / total) * 100;

    const displayProgress = useMemo(() => {
        if (active) return Math.round(downloadProgress * 0.5);
        return Math.min(Math.round(50 + compileProgress * 0.5), 99);
    }, [active, downloadProgress, compileProgress]);

    useEffect(() => {
        if (!active && loaded === total && total > 0) {
            const t = setTimeout(() => setIsReadyToStart(true), 200);
            return () => clearTimeout(t);
        }
    }, [active, loaded, total]);

    const handleStart = () => {
        if (!isReadyToStart || gpuError) return;
        setIsGameStarted(true);
        if (containerRef.current) {
            animationRef.current = gsap.to(containerRef.current, {
                opacity: 0,
                duration: 1,
                ease: "power2.inOut",
                onComplete: () => setIsVisible(false)
            });
        }
    };

    // 從心遊語宙降落時，自動開始並淡出白屏
    useEffect(() => {
        if (fromStar && isReadyToStart && !gpuError) {
            // 移除 index.html 中隱藏 Canvas 的樣式
            document.getElementById('xingye-landing')?.remove();
            setIsGameStarted(true);
            if (containerRef.current) {
                animationRef.current = gsap.to(containerRef.current, {
                    opacity: 0,
                    duration: 0.8,
                    ease: "power2.inOut",
                    onComplete: () => setIsVisible(false)
                });
            }
        }
    }, [fromStar, isReadyToStart, gpuError]);

    // 預計算降落星軌與星點（必須在所有條件分支前呼叫，遵守 React Hooks 規則）
    const landingTrails = useMemo(() =>
        Array.from({ length: 30 }, () => ({
            left: `${Math.random() * 100}%`,
            delay: `${Math.random() * 0.8}s`,
            duration: `${0.7 + Math.random() * 0.5}s`,
            width: 0.5 + Math.random() * 1,
            height: 30 + Math.random() * 80,
            opacity: 0.15 + Math.random() * 0.35,
        })), []);
    const landingStars = useMemo(() =>
        Array.from({ length: 60 }, () => ({
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            delay: `${Math.random() * 0.8}s`,
            duration: `${0.6 + Math.random() * 0.8}s`,
            size: 1 + Math.random() * 2,
        })), []);

    useEffect(() => {
        return () => {
            if (animationRef.current) animationRef.current.kill();
        };
    }, []);

    if (!isVisible) return null;

    // ── 從心遊語宙降落：星流過場畫面（與 logodrift 銜接） ──
    if (fromStar) {
        const starColor = useGameStore.getState().starColor || '#ffffff';
        return (
            <div ref={containerRef} style={{
                position: 'fixed', top: 0, left: 0,
                width: '100vw', height: '100dvh',
                background: `radial-gradient(ellipse at center, ${starColor} 0%, ${starColor}40 20%, #000 60%, #000 100%)`,
                zIndex: 9999,
                overflow: 'hidden',
                pointerEvents: 'auto',
            }}>
                {/* 星軌長條 */}
                {landingTrails.map((t, i) => (
                    <div key={`trail-${i}`} style={{
                        position: 'absolute',
                        left: t.left, top: '-10%',
                        width: `${t.width}px`, height: `${t.height}px`,
                        background: `linear-gradient(to bottom, transparent, rgba(255,255,255,${t.opacity}), transparent)`,
                        borderRadius: '1px',
                        animation: `xingye-trail-fall ${t.duration} linear ${t.delay} infinite`,
                    }} />
                ))}
                {/* 星流粒子 */}
                {landingStars.map((p, i) => (
                    <div key={i} style={{
                        position: 'absolute',
                        left: p.left, top: p.top,
                        width: `${p.size}px`, height: `${p.size}px`,
                        background: '#fff', borderRadius: '50%',
                        boxShadow: `0 0 ${p.size * 3}px rgba(255,255,255,0.9)`,
                        animation: `xingye-starfall ${p.duration} linear ${p.delay} infinite`,
                    }} />
                ))}

                {/* 文字與進度 */}
                <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: '16px',
                }}>
                    <p style={{
                        fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)',
                        letterSpacing: '0.35em',
                        fontFamily: '"Noto Serif TC", serif',
                        textShadow: `0 0 20px ${starColor}60, 0 0 40px ${starColor}30`,
                        animation: 'xingye-pulse 2s infinite',
                    }}>
                        降落中…
                    </p>
                    <div style={{
                        width: '160px', height: '2px',
                        background: 'rgba(255,255,255,0.15)', borderRadius: '1px',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${displayProgress}%`,
                            height: '100%',
                            background: 'rgba(255,255,255,0.5)',
                            borderRadius: '1px',
                            transition: 'width 0.3s ease',
                        }} />
                    </div>
                </div>

                <style>{`
                    @keyframes xingye-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
                    @keyframes xingye-starfall {
                        0% { transform: translateY(-20vh) scale(0.5); opacity: 0; }
                        10% { opacity: 1; }
                        100% { transform: translateY(120vh) scale(2); opacity: 0; }
                    }
                    @keyframes xingye-trail-fall {
                        0% { transform: translateY(0) scaleY(0.3); opacity: 0; }
                        15% { opacity: 1; }
                        100% { transform: translateY(130vh) scaleY(1.5); opacity: 0; }
                    }
                `}</style>
            </div>
        );
    }

    // --- Dynamic Styles ---
    const isMobileLandscape = isMobile && isLandscape;

    const containerStyle: React.CSSProperties = {
        position: 'fixed', top: 0, left: 0,
        width: '100vw', height: '100dvh',
        background: '#000', zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontFamily: 'Cousine',
        pointerEvents: 'auto',
        fontSize: isMobile ? '0.8rem' : '0.9rem',
        opacity: 0.99,
        // In landscape, we want strict overflow handling
        overflow: 'hidden',
        padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
    };

    const entryContainerStyle: React.CSSProperties = {
        opacity: 1,
        maxWidth: isMobileLandscape ? '80%' : (isMobile ? '100%' : '600px'),
        padding: isMobileLandscape ? '20px' : '40px',
        animation: 'fadeIn 2s ease',
        display: 'flex',
        // SWITCH LAYOUT: Row for landscape, Column for portrait
        flexDirection: isMobileLandscape ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobileLandscape ? '40px' : '0px',
        height: isMobileLandscape ? '100%' : 'auto'
    };

    const playButtonStyle: React.CSSProperties = {
        color: gpuError ? '#ff4444' : 'white',
        backgroundColor: 'transparent',
        border: 'none',
        letterSpacing: '3px',
        transition: 'all 0.5s ease',
        transform: 'scale(1)',
        cursor: gpuError ? 'default' : (isReadyToStart ? 'pointer' : 'wait'),
        opacity: gpuError ? 0.8 : 1,
        whiteSpace: 'nowrap',
        animation: isReadyToStart ? 'breathe 2s infinite ease-in-out' : 'none',
    };

    return (
        <div ref={containerRef} style={containerStyle}>
            <div className='entry' style={entryContainerStyle}>

                {/* Left Side: Content Text */}
                <div style={{
                    flex: isMobileLandscape ? '1' : 'auto',
                    textAlign: isMobileLandscape ? 'left' : 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isMobileLandscape ? 'center' : 'flex-start'
                }}>
                    {/* Title */}
                    <div style={{
                        fontSize: '1rem', fontWeight: 'bold',
                        letterSpacing: isMobile ? '0.3rem' : '0.5rem',
                        marginBottom: isMobileLandscape ? '1rem' : '2rem',
                    }}>
                        {fromStar ? `降落 · ${fromStar}` : '星野 XINGYE'}
                    </div>

                    {/* Intro Text */}
                    <div style={{
                        textAlign: 'left',
                        display: 'inline-block',
                        lineHeight: '1.5', color: '#ccc',
                        marginBottom: isMobileLandscape ? '0' : '3rem',
                        fontSize: isMobileLandscape ? '0.75rem' : 'inherit',
                    }}>
                        <p>
                            After drifting beyond the edge of space, the journey reaches ground again.
                            A surface extends in every direction, without a visible boundary.
                            No matter how far it continues, the horizon does not draw closer.
                        </p>

                        <p>
                            With each step, something descends and alters the surface, leaving traces behind.
                            The drift does not end here; it continues in another form.
                        </p>
{/* 
                        {!gpuError && (
                            <p>
                                As you travel forward, the ground begins to change beneath you, leaving visible traces of passage behind.
                            </p>
                        )} */}
                    </div>
                </div>

                {/* Right Side: Interaction Area */}
                <div style={{
                    flex: isMobileLandscape ? '0.8' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: isMobileLandscape ? '200px' : 'auto'
                }}>
                    {/* Play Button & Progress Bar */}
                    <div className='play'>
                        <button
                            onClick={handleStart}
                            disabled={!isReadyToStart || !!gpuError}
                            style={playButtonStyle}
                            onMouseEnter={(e) => (isReadyToStart && !gpuError) && (e.currentTarget.style.transform = 'scale(1.02)')}
                            onMouseLeave={(e) => (isReadyToStart && !gpuError) && (e.currentTarget.style.transform = 'scale(1)')}
                        >
                            {gpuError ? (
                                <span style={{ letterSpacing: '2px' }}>SYSTEM INCOMPATIBLE</span>
                            ) : isReadyToStart ? (
                                "[ START ]"
                            ) : (
                                <span>
                                    {active ? "LOADING" : "CALIBRATING"}... {displayProgress}%
                                </span>
                            )}
                        </button>

                        <div style={{
                            width: '100%', maxWidth: '250px', height: '1px', background: '#222', margin: '10px auto',
                            opacity: (isReadyToStart || gpuError) ? 0 : 1, transition: 'opacity 0.5s'
                        }}>
                            <div style={{ width: `${displayProgress}%`, height: '100%', background: '#666', transition: 'width 0.2s' }} />
                        </div>
                    </div>

                    {/* Bottom Area: Controls */}
                    <div style={{
                        marginTop: isMobileLandscape ? '15px' : '40px',
                        color: '#ccc', opacity: 0.8, animation: 'fadeIn 3s ease',
                        userSelect: 'none', display: 'flex', justifyContent: 'center', gap: '24px',
                        flexDirection: 'row',
                    }}>
                        {gpuError ? (
                            <div style={{ fontSize: '0.8rem', maxWidth: '400px', lineHeight: '1.4', textAlign: 'center' }}>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.7rem' }}>ERROR CODE: {gpuError}</p>
                            </div>
                        ) : (
                            isMobile ? (
                                <>
                                    <InstructionRow input={<Key>L-STICK</Key>} label="MOVE" />
                                    {/* Hide 'Touch' instruction on very small landscape screens if crowded */}
                                    <InstructionRow input={<Key>TOUCH</Key>} label="LOOK" />
                                </>
                            ) : (
                                <>
                                    <InstructionRow input={<><Key>W</Key><Key>A</Key><Key>S</Key><Key>D</Key></>} label="MOVE" />
                                    <InstructionRow input={<Key>SHIFT</Key>} label="RUN" />
                                    <InstructionRow input={<Key>C</Key>} label="CAM" />
                                    <InstructionRow input={<MouseIcon />} label="LOOK" />
                                </>
                            )
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}